<template>
	<div v-loading="loading" class="config-panel">
		<gc-sub-title icon="icon-gaojing1" :text="title">
			<el-button class="save-btn" type="primary" plain circle @click="handleSaveClick">保存</el-button>
		</gc-sub-title>
		<el-form ref="formRef" class="config-form" label-position="left" :model="formData">
			<el-form-item label="是否配置该告警项" class="switch-form-item">
				<el-radio-group v-model="formData.eventSwitch">
					<el-radio label="1">是</el-radio>
					<el-radio label="0">否</el-radio>
				</el-radio-group>
			</el-form-item>
			<el-form-item>
				<p class="title">配置信息</p>
				<ItemForm
					v-model="formData.configInfoList"
					ref="itemFormRef"
					class="item-form"
					:disabled="formData.eventSwitch === '0'"
					:event-expression="eventExpression"
					:show-comparison="showComparison"
				/>
			</el-form-item>
			<el-form-item class="event-list-form-item">
				<div class="title">联动事件</div>
				<el-checkbox-group v-model="formData.selectedEvents">
					<el-checkbox
						v-for="item in eventList"
						:key="item.value"
						:label="item.value"
						class="checkbox-item"
						:disabled="formData.eventSwitch === '0'"
					>
						{{ item.label }}
					</el-checkbox>
				</el-checkbox-group>
			</el-form-item>
		</el-form>
	</div>
</template>

<script>
import { apiGetAlarmConfigDetail, apiEditCommonAlarmConfig } from '@/apis/waterAlarmConfig.api'
import ItemForm from './ItemForm.vue'

const SWITCH_STATUS = {
	active: '1',
	deactive: '0',
}

export default {
	name: 'ConfigPanel',
	components: {
		ItemForm,
	},
	props: {
		title: {
			type: String,
			default: '',
		},
		id: {
			type: Number,
		},
		classification: {
			type: String,
			default: '',
		},
		eventName: {
			type: String,
			default: '',
		},
		eventExpression: {
			type: String,
			default: '',
		},
		showComparison: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			loading: false,
			formData: {
				selectedEvents: [],
				configInfoList: [],
				eventSwitch: '1',
			},
			eventList: [
				{ label: '员工微信推送', value: 'pushWechat', prop: 'pushWechat' },
				{ label: '员工短信推送', value: 'pushSms', prop: 'pushSms' },
				{ label: '页面推送', value: 'pushPage', prop: 'pushPage' },
				{ label: '声音告警', value: 'pushVoice', prop: 'pushVoice' },
			],
		}
	},
	watch: {
		id: {
			handler(newVal) {
				this.updateConfigInfo(newVal)
			},
		},
	},
	activated() {
		// 当重新进入页面时，重新请求数据
		if (!this.id) {
			return
		}
		this.updateConfigInfo(this.id)
	},
	methods: {
		handleSaveClick() {
			if (!this.$refs.itemFormRef.isValid()) {
				this.$message.warning('请填写完整信息')
				return
			}
			this.$confirm('是否保存设置？', '提示', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning',
			})
				.then(() => {
					this.saveConfig()
				})
				.catch(() => {})
		},
		saveConfig() {
			const params = this.generateParams(this.formData, this.showComparison)
			this.loading = true
			apiEditCommonAlarmConfig(params)
				.then(() => {
					this.$message.success('保存成功')
				})
				.catch(() => {})
				.finally(() => {
					this.loading = false
					this.$emit('update')
				})
		},
		async updateConfigInfo(eventId) {
			this.loading = true
			const res = await this.getConfigInfo({ eventId })
			this.formData = this.dealRes(res)
			this.loading = false
		},
		getConfigInfo(params) {
			return apiGetAlarmConfigDetail(params)
				.then(res => res ?? {})
				.catch(() => ({}))
		},
		dealRes(obj) {
			return {
				eventSwitch: obj.eventSwitch ?? '0',
				configInfoList: this.dealConfigInfoList(obj.waterAlarmConfigRspDetailVOList),
				selectedEvents: this.dealSelectedEvents(obj),
			}
		},
		dealConfigInfoList(waterSysAlarmConfig) {
			return (
				waterSysAlarmConfig?.map(item => ({
					// 不走修改，全部新增
					// eventModelConfigId: item.eventModelConfigId,
					userTypeId: item.userTypeId,
					comparisonValue: item.comparisonValue,
					eventLevel: item.eventLevel,
				})) ?? [
					{
						eventLevel: this.$store.getters.dataList.alarmLevel[0].defaultValue,
					},
				]
			)
		},
		dealSelectedEvents(obj) {
			const pushConfig = {
				pushWechat: obj.pushWechat,
				pushSms: obj.pushSms,
				pushPage: obj.pushPage,
				pushVoice: obj.pushVoice,
			}
			return Object.entries(pushConfig)
				.map(([key, value]) => (value === SWITCH_STATUS.active ? key : ''))
				.filter(item => !!item)
		},
		generateParams(formData, showComparison) {
			return {
				eventId: this.id,
				eventName: this.eventName,
				classification: this.classification,
				eventSwitch: formData.eventSwitch,
				waterSysAlarmConfig: this.generateWaterSysAlarmConfig(formData.configInfoList, showComparison),
				pushCreateBIds: this.generatePushConfig(formData.selectedEvents),
			}
		},
		generateWaterSysAlarmConfig(configInfoList, showComparison) {
			return configInfoList.map(item => {
				// eslint-disable-next-line no-unused-vars
				const { selfId, comparisonValue, ...others } = item
				return showComparison ? { comparisonValue, ...others } : others
			})
		},
		generatePushConfig(selectedEvents) {
			return ['pushWechat', 'pushSms', 'pushPage', 'pushVoice']
				.map((key, index) => (selectedEvents.includes(key) ? index + 1 : undefined))
				.filter(item => item !== undefined)
				.join(',')
		},
	},
}
</script>

<style scoped lang="scss">
::v-deep .el-checkbox-group {
	display: flex;
	flex-direction: column;
	width: fit-content;
}

::v-deep {
	.switch-form-item .el-form-item__label {
		color: #b0b0b0;
	}
	.event-list-form-item .el-checkbox__label {
		color: #64666a;
	}
}

::v-deep {
	.el-button.save-btn {
		width: 100px;
		height: 32px;
		border-radius: 16px !important;
	}
}

.config-panel {
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 25px;
	overflow: hidden;
}

.config-form {
	flex: 1;
	padding: 25px;
	overflow: auto;
}

.title {
	margin-top: 30px;
	color: #ababab;
	position: relative;
	&::after {
		content: '';
		position: absolute;
		left: 60px;
		right: 0;
		top: 50%;
		transform: translateY(-50%);
		border-bottom: 1px solid #eef0f3;
	}
}

.item-form {
	padding: 0 10px;
}

.event-list-form-item {
	padding-left: 10px;
	padding-right: 20%;
}
</style>
